import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  MapPin, Maximize, Euro, TrendingUp, Home, Building2, 
  LandPlot, Phone, ArrowLeft, ChevronLeft, ChevronRight,
  Banknote, PiggyBank, CheckCircle, Info, Sparkles, ChevronDown,
  FileText, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import InvestorReport from '../components/InvestorReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const downloadReport = async () => {
    if (!id) return;
    setDownloadingReport(true);
    try {
      const res = await axios.get(`${API}/properties/${id}/report-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const slug = (property?.title || 'bien').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
      a.download = `rapport-pro-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Report download error:', e);
      alert("Impossible de générer le rapport pour le moment. Réessayez ou contactez-nous.");
    } finally {
      setDownloadingReport(false);
    }
  };

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  useEffect(() => {
    if (property?.title) {
      document.title = `${property.title} | Investissement Immobilier | G Division`;
    }
  }, [property]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Appartement': return Building2;
      case 'Maison': return Home;
      case 'Terrain': return LandPlot;
      default: return Building2;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const calculatePricePerSqm = () => {
    if (!property?.price || !property?.surface) return null;
    return Math.round(property.price / property.surface);
  };

  const nextImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-600 mb-4">Bien non trouvé</h1>
        <Link to="/nos-biens">
          <Button>Retour aux biens</Button>
        </Link>
      </div>
    );
  }

  const TypeIcon = getTypeIcon(property.property_type);
  const pricePerSqm = calculatePricePerSqm();

  return (
    <>
      <div className="pt-20 bg-gray-50 min-h-screen" data-testid="property-detail-page">
        {/* Back Button */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            <Link to="/nos-biens" className="inline-flex items-center text-gray-600 hover:text-[#1e3a5f] transition-colors">
              <ArrowLeft size={20} className="mr-2" />
              Retour aux biens
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Left: Images */}
            <div>
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden bg-gray-200 aspect-[4/3] mb-4">
                {property.images && property.images.length > 0 ? (
                  <>
                    <img 
                      src={property.images[currentImageIndex]} 
                      alt={property.title}
                      className={`w-full h-full object-cover ${property.status === 'Vendu' ? 'grayscale' : ''}`}
                    />
                    
                    {/* VENDU Overlay */}
                    {property.status === 'Vendu' && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="bg-red-600 text-white text-4xl font-black px-12 py-4 rounded-lg transform -rotate-12 shadow-lg uppercase tracking-wider">
                          VENDU
                        </div>
                      </div>
                    )}

                    {/* Navigation Arrows */}
                    {property.images.length > 1 && (
                      <>
                        <button 
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button 
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                        >
                          <ChevronRight size={24} />
                        </button>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {property.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <TypeIcon className="text-gray-400" size={80} />
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {property.images && property.images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {property.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index ? 'border-[#c9a227]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Details */}
            <div>
              {/* Status Badge */}
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-4 py-2 rounded-lg text-sm font-bold ${
                  property.status === 'Disponible' ? 'bg-green-100 text-green-700' :
                  property.status === 'Sous compromis' ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {property.status === 'Disponible' ? '✓ DISPONIBLE' : property.status.toUpperCase()}
                </span>
                <span className="text-[#c9a227] font-medium flex items-center gap-1">
                  <TypeIcon size={18} />
                  {property.property_type}
                </span>
              </div>

              {/* Title */}
              <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
                {property.title}
              </h1>

              {/* Location */}
              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <MapPin size={20} />
                <span className="text-lg">{property.location}</span>
              </div>

              {/* Price */}
              <div className="bg-[#1e3a5f] text-white rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-300 uppercase">Prix de vente</p>
                    <p className="text-4xl font-bold">{formatPrice(property.price)}</p>
                  </div>
                  {property.roi && (
                    <div className="bg-[#c9a227] text-white px-4 py-2 rounded-xl">
                      <p className="text-xs uppercase">Rendement</p>
                      <p className="text-2xl font-bold">{property.roi}%</p>
                    </div>
                  )}
                </div>
                
                {pricePerSqm && (
                  <p className="text-gray-300 text-sm">
                    Soit {formatPrice(pricePerSqm)}/m²
                  </p>
                )}
              </div>

              {/* Investment Metrics — Loyer + ROI */}
              {(property.estimated_rent || property.roi) && property.status !== 'Vendu' && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                      <TrendingUp className="text-green-600" size={20} />
                      Simulation Investissement
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {property.estimated_rent && (
                      <div className="bg-white rounded-xl p-4">
                        <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2">
                          <Banknote size={14} />
                          Loyer estimé
                        </div>
                        <p className="text-2xl md:text-3xl font-bold text-[#1e3a5f] leading-none">
                          {property.estimated_rent}€
                          <span className="text-sm font-normal text-gray-500"> /mois</span>
                        </p>
                      </div>
                    )}

                    {property.roi && (
                      <div className="bg-white rounded-xl p-4">
                        <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2">
                          <TrendingUp size={14} />
                          Rendement brut
                          <Popover>
                            <PopoverTrigger asChild>
                              <button 
                                type="button" 
                                className="ml-auto text-gray-400 hover:text-[#c9a227] transition-colors"
                                aria-label="Qu'est-ce que le rendement brut ?"
                                data-testid="roi-info-trigger"
                              >
                                <Info size={14} />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-4" side="top">
                              <h4 className="font-semibold text-[#1e3a5f] mb-2 flex items-center gap-2">
                                <TrendingUp size={16} className="text-[#c9a227]" />
                                Qu'est-ce que le ROI brut&nbsp;?
                              </h4>
                              <p className="text-sm text-gray-600 mb-3">
                                Le <strong>rendement locatif brut</strong> mesure la performance d'un investissement immobilier :
                              </p>
                              <div className="bg-gray-50 rounded-lg p-3 mb-3 text-center">
                                <p className="text-xs text-gray-500 mb-1">Formule</p>
                                <p className="text-sm font-mono font-semibold text-[#1e3a5f]">
                                  (Loyer annuel / Prix) × 100
                                </p>
                              </div>
                              <p className="text-xs font-semibold text-gray-700 mb-2">À titre de comparaison&nbsp;:</p>
                              <ul className="space-y-1.5 text-xs">
                                <li className="flex justify-between items-center py-1 border-b border-gray-100">
                                  <span className="text-gray-600">Livret A</span>
                                  <span className="font-semibold text-gray-700">1,5%</span>
                                </li>
                                <li className="flex justify-between items-center py-1 border-b border-gray-100">
                                  <span className="text-gray-600">Assurance-vie (fonds €)</span>
                                  <span className="font-semibold text-gray-700">~2,5%</span>
                                </li>
                                <li className="flex justify-between items-center py-1 border-b border-gray-100">
                                  <span className="text-gray-600">SCPI (moyenne FR)</span>
                                  <span className="font-semibold text-gray-700">4,5 – 6%</span>
                                </li>
                                <li className="flex justify-between items-center py-1 border-b border-gray-100">
                                  <span className="text-gray-600">Locatif classique</span>
                                  <span className="font-semibold text-gray-700">3 – 5%</span>
                                </li>
                                <li className="flex justify-between items-center py-1 bg-[#c9a227]/10 px-2 rounded mt-2">
                                  <span className="text-[#1e3a5f] font-semibold">Ce bien</span>
                                  <span className="font-bold text-[#c9a227]">{property.roi}%</span>
                                </li>
                              </ul>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <p className="text-2xl md:text-3xl font-bold text-[#c9a227] leading-none">
                          {property.roi}%
                        </p>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                    * Chiffres indicatifs hors charges, fiscalité et financement. Pour une simulation complète et personnalisée selon votre situation, contactez-nous.
                  </p>
                </div>
              )}

              {/* Specs */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <Maximize className="mx-auto text-[#1e3a5f] mb-2" size={24} />
                  <p className="text-2xl font-bold text-[#1e3a5f]">{property.surface}</p>
                  <p className="text-sm text-gray-500">m²</p>
                </div>
                {property.rooms && (
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <Home className="mx-auto text-[#1e3a5f] mb-2" size={24} />
                    <p className="text-2xl font-bold text-[#1e3a5f]">{property.rooms}</p>
                    <p className="text-sm text-gray-500">pièces</p>
                  </div>
                )}
                {pricePerSqm && (
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <Euro className="mx-auto text-[#1e3a5f] mb-2" size={24} />
                    <p className="text-2xl font-bold text-[#1e3a5f]">{pricePerSqm}</p>
                    <p className="text-sm text-gray-500">€/m²</p>
                  </div>
                )}
              </div>

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#1e3a5f] mb-3">Caractéristiques</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.features.map((feature, index) => (
                      <span 
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                      >
                        <CheckCircle size={14} className="text-green-500" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              {property.status !== 'Vendu' && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href={`https://wa.me/33645040975?text=Bonjour, je suis intéressé par le bien "${property.title}" à ${property.location} (${formatPrice(property.price)}). Pouvez-vous me donner plus d'informations ?`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg font-semibold rounded-xl">
                      <Phone size={20} className="mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                  <Link to="/contact" className="flex-1">
                    <Button variant="outline" className="w-full border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white py-6 text-lg font-semibold rounded-xl">
                      Demander un RDV
                    </Button>
                  </Link>
                </div>
              )}

              {/* Download Pro Report */}
              {property.status !== 'Vendu' && (
                <Button
                  onClick={downloadReport}
                  disabled={downloadingReport}
                  className="w-full mt-3 bg-white border-2 border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227] hover:text-white py-5 text-base font-semibold rounded-xl transition-colors group"
                  data-testid="download-property-report-btn"
                >
                  {downloadingReport ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin mr-2 group-hover:border-white group-hover:border-t-transparent" />
                      Génération du rapport…
                    </>
                  ) : (
                    <>
                      <FileText size={18} className="mr-2" />
                      Télécharger le rapport pro
                      <Download size={16} className="ml-2 opacity-70" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Investor Report Toggle Button — Highly Visible */}
          {property.status !== 'Vendu' && property.price > 0 && (
            <div className="mt-6 relative">
              {/* Glow halo */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#c9a227]/30 via-emerald-500/30 to-[#c9a227]/30 blur-2xl rounded-3xl pointer-events-none animate-pulse"></div>
              <button
                onClick={() => setShowReport((v) => !v)}
                className="group relative w-full bg-gradient-to-r from-[#1e3a5f] via-[#0f1d2f] to-[#1e3a5f] hover:from-[#162a45] hover:to-[#162a45] text-white rounded-2xl p-5 md:p-7 flex items-center justify-between transition-all duration-300 border-2 border-[#c9a227]/50 hover:border-[#c9a227] shadow-2xl shadow-[#c9a227]/20 hover:shadow-[0_0_50px_rgba(201,162,39,0.4)] overflow-hidden"
                data-testid="toggle-investor-report-btn"
              >
                {/* Animated shimmer */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none"></div>
                {/* Top NEW badge */}
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider animate-pulse z-10">
                  <Sparkles size={9} />
                  Nouveau
                </span>

                <div className="flex items-center gap-4 md:gap-5 relative z-10">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#c9a227] via-[#e6c14d] to-[#a8861e] rounded-2xl flex items-center justify-center shadow-lg shadow-[#c9a227]/40 group-hover:scale-110 transition-transform duration-300">
                    <Sparkles className="text-white" size={28} />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-[#c9a227] mb-1">Rapport Investisseur · Pro</p>
                    <p className="font-['Playfair_Display'] text-xl md:text-2xl font-bold leading-tight">
                      {showReport ? 'Masquer le rapport' : 'Voir le rapport investisseur détaillé'}
                    </p>
                    <p className="text-xs text-gray-300 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1"><TrendingUp size={12} className="text-[#c9a227]" /> ROI dynamique</span>
                      <span className="inline-flex items-center gap-1"><PiggyBank size={12} className="text-emerald-400" /> Projection 10 ans</span>
                      <span className="inline-flex items-center gap-1"><Building2 size={12} className="text-white" /> 3 stratégies</span>
                    </p>
                  </div>
                </div>
                <ChevronDown className={`text-[#c9a227] transition-transform duration-300 flex-shrink-0 relative z-10 ${showReport ? 'rotate-180' : ''}`} size={28} />
              </button>
            </div>
          )}

          {/* Investor Report (rendered separately so the button stays visible) */}
          {showReport && property.status !== 'Vendu' && property.price > 0 && (
            <InvestorReport property={property} />
          )}

          {/* Description */}
          <div className="mt-8 bg-white rounded-2xl p-6 md:p-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-4">
              Description
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {property.description}
            </p>
          </div>

          {/* Why Invest Section */}
          {property.status !== 'Vendu' && (
            <div className="mt-8 bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] rounded-2xl p-6 md:p-8 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold mb-6">
                Pourquoi Investir avec G Division ?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a227]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-[#c9a227]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Rendements Optimisés</h3>
                    <p className="text-gray-300 text-sm">Biens issus de nos divisions = prix inférieurs au marché</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a227]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-[#c9a227]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Clé en Main</h3>
                    <p className="text-gray-300 text-sm">Accompagnement complet jusqu'à la mise en location</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c9a227]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <PiggyBank className="text-[#c9a227]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Cashflow Positif</h3>
                    <p className="text-gray-300 text-sm">Des biens qui s'autofinancent dès le premier mois</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PropertyDetail;
